// docs/61 EX13–EX14: an exploration's dressing on the board — its wonders (each look an Oryx frame,
// a tint and a small vector touch; a glimmer of sparks while unbeheld, a soft ring once beheld, and
// a stir that keeps it alive: a creature breathes and turns, a plant sways, a light pulses, a
// spirit hovers), its veils (banks of drifting mist on the trail that blow apart when parted), its
// ways (the trail running on into the mist at the board's edge, named once sighted), and the air
// (motes drifting over the whole board and a faint wash of the air's light).
// docs/138 XO8: an exploration's camps — each dressed on its squares by its `dress` (a cookfire
// that flickers, tents, webs, a nest, burrows, bones, totems, wagons, a hive, a dark pool, a
// ruin, an altar; Oryx frames where the sheet has them, small vector stand-ins where it has not),
// its name hung over its middle once seen, and a drifting "z" over every foe still asleep in it.
// New explorations carry no ways or veils, so those loops simply draw nothing.
//
// Loops are driven from `update(time)` rather than tweens: the scene kills every tween when a new
// board arrives, and this layer redraws in the middle of a playback when a veil parts.
import Phaser from 'phaser';
import type { CrawlView, ExploreCamp, ExploreView, FixtureView, Tile, TileData, UnitState } from '../../core/sim/types';
import { airOf, awakeFoes, campDressOf, hash01, lookOf, wonderName, type LookDef } from '../../core/explore/explore';
import { atlasForFrame } from '../assets';
import { PUZZLE_ATLAS } from './puzzleArt';
import { fieldFade, mergeRuns, type FieldShape } from './field';
import { oneShot } from './oneShots';

const GOLD = 0xffd166;

interface WonderSprite { id: number; tile: Tile; look: LookDef; found: boolean; body?: Phaser.GameObjects.Image; sheen?: Phaser.GameObjects.Image; parts: Phaser.GameObjects.Components.Transform[]; glow: Phaser.GameObjects.Image; sparks: Phaser.GameObjects.Image[]; ring?: Phaser.GameObjects.Graphics; extra?: Phaser.GameObjects.Graphics; seed: number }
interface Flicker { img: Phaser.GameObjects.Image; frames: string[]; seed: number; glow?: Phaser.GameObjects.Image }
interface Snore { txt: Phaser.GameObjects.Text; x: number; y: number; seed: number }
interface Puff { img: Phaser.GameObjects.Image; x: number; y: number; r: number; seed: number; veil: number; base: number }

function ensureTextures(scene: Phaser.Scene): void {
  const tm = scene.textures;
  const radial = (key: string, size: number, stops: Array<[number, number]>) => {
    if (tm.exists(key)) return;
    const c = tm.createCanvas(key, size, size)!; const x = c.getContext();
    const g = x.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    for (const [at, a] of stops) g.addColorStop(at, `rgba(255,255,255,${a})`);
    x.fillStyle = g; x.fillRect(0, 0, size, size); c.refresh();
  };
  radial('fk-x-puff', 64, [[0, 0.9], [0.45, 0.55], [1, 0]]);
  radial('fk-x-glow', 64, [[0, 0.75], [0.4, 0.3], [1, 0]]);
  radial('fk-x-mote', 8, [[0, 1], [0.5, 0.6], [1, 0]]);
  if (!tm.exists('fk-x-spark')) {
    // a little four-point star
    const c = tm.createCanvas('fk-x-spark', 7, 7)!; const x = c.getContext();
    x.fillStyle = 'rgba(255,255,255,0.55)'; x.fillRect(3, 0, 1, 7); x.fillRect(0, 3, 7, 1);
    x.fillStyle = '#fff'; x.fillRect(3, 2, 1, 3); x.fillRect(2, 3, 3, 1); c.refresh();
  }
  if (!tm.exists('fk-x-petal')) {
    const c = tm.createCanvas('fk-x-petal', 3, 2)!; const x = c.getContext();
    x.fillStyle = '#fff'; x.fillRect(0, 0, 2, 1); x.fillRect(1, 1, 2, 1); c.refresh();
  }
}

export class ExploreLayer {
  private objects: Phaser.GameObjects.GameObject[] = [];
  private wonders: WonderSprite[] = [];
  private puffs: Puff[] = [];
  private flickers: Flicker[] = [];
  private snores: Snore[] = [];
  private emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private wash?: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Graphics;
  private airKey = '';
  /** docs/118 PT12: the field the party has seen, when the fog hides the rest (a crawl's bones). */
  private seen?: FieldShape;
  private seenKey = '';
  private lastAir?: { air: number | undefined; board: { w: number; h: number } };
  private reduced = false;
  /** Veils blowing apart: index → when it began (ms, scene time). */
  private parting = new Map<number, number>();
  private now = 0;

  constructor(private readonly scene: Phaser.Scene, private readonly layer: Phaser.GameObjects.Layer, private readonly high: Phaser.GameObjects.Layer, private readonly cell: number, private readonly dpr = 1) {
    ensureTextures(scene);
  }

  setReducedMotion(on: boolean): void { this.reduced = on; }

  private add<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.layer.add(o);
    this.objects.push(o);
    return o;
  }

  private image(frame: string, x: number, y: number, size: number): Phaser.GameObjects.Image | undefined {
    const atlas = atlasForFrame(frame);
    if (!this.scene.textures.exists(atlas) || !this.scene.textures.get(atlas).has(frame)) return undefined;
    const img = this.scene.add.image(x, y, atlas, frame);
    img.setScale(size / Math.max(1, img.width));
    return this.add(img);
  }

  private label(t: Tile, text: string, colour: string, dy = 0): void {
    if (!text) return;
    const txt = this.scene.add.text(t.x * this.cell + this.cell / 2, t.y * this.cell + this.cell + 0.5 + dy, text, {
      fontFamily: 'Cinzel, Georgia, serif', fontSize: '6px', fontStyle: '700', color: colour, stroke: '#0b0f1a', strokeThickness: 2,
    }).setOrigin(0.5, 0).setResolution(Math.max(4, this.dpr * 4));
    this.add(txt);
  }

  /**
   * Redraw the wonders, veils and ways of `x` (the party's view). A wonder is drawn where its
   * fixture stands in the room the party sees; the fog over unexplored ground hides the rest.
   */
  draw(board: { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> }, x: ExploreView | undefined, fixtures?: readonly FixtureView[], more?: { crawl?: CrawlView; units?: readonly UnitState[] }): void {
    for (const o of this.objects) o.destroy();
    this.objects = [];
    this.wonders = [];
    this.puffs = [];
    this.flickers = [];
    this.snores = [];
    if (!x) { this.setAir(undefined, board); return; }
    this.setAir(x.air, board);
    const c = this.cell;
    const byId = new Map((fixtures ?? []).map((f) => [f.id, f] as const));
    // docs/138 XO8: the camps, under everything else, and the sleepers in them
    for (const camp of x.camps) this.camp(camp);
    this.sleepers(more?.crawl, more?.units);
    // the ways first: trail running off into the mist at the edge, under everything else
    x.ways.forEach((w) => {
      if (!w.tiles.length) return;
      const g = this.add(this.scene.add.graphics());
      if (w.sighted) {
        // a waymarker: a small stone with a gold chevron pointing off the board
        const mid = w.tiles[Math.floor(w.tiles.length / 2)]!;
        const out = edgeOut(mid, board.w, board.h);
        const mx = mid.x * c + c / 2, my = mid.y * c + c / 2;
        g.fillStyle(0x0b0f1a, 0.75).fillCircle(mx, my, 4.5);
        g.fillStyle(GOLD, 1);
        g.fillTriangle(mx + out.x * 3.5 + out.y * 0, my + out.y * 3.5, mx - out.y * 2.6 - out.x * 1.5, my + out.x * 2.6 - out.y * 1.5, mx + out.y * 2.6 - out.x * 1.5, my - out.x * 2.6 - out.y * 1.5);
        this.label(mid, w.name.replace(/^the /i, ''), '#ffe39a');
      }
      // the trail runs on into the mist: soft banks thickening toward the edge it leaves by
      for (let i = 0; i < w.tiles.length; i++) {
        const t = w.tiles[i]!;
        const out = edgeOut(t, board.w, board.h);
        for (let k = 0; k < 2; k++) {
          const f = 0.2 + 0.6 * k;
          const px = t.x * c + c / 2 + out.x * c * f + (hash01(t.x, t.y, k) - 0.5) * c * 0.4, py = t.y * c + c / 2 + out.y * c * f + (hash01(t.x, t.y, k + 4) - 0.5) * c * 0.4;
          const r = c * (1.3 + 0.6 * k), base = 0.3 + 0.3 * k;
          const img = this.add(this.scene.add.image(px, py, 'fk-x-puff').setDisplaySize(r, r).setAlpha(base).setTint(0xf0f4fa));
          this.puffs.push({ img, x: px, y: py, r, seed: hash01(t.x, t.y, 9 + k) * 1000, veil: -1, base });
        }
      }
    });
    // veils: banks of mist on their squares (blowing apart while parting, faint wisps once parted)
    x.veils.forEach((v, vi) => {
      for (const t of v.tiles) {
        for (let k = 0; k < 4; k++) {
          const px = t.x * c + c * (0.2 + 0.6 * hash01(t.x, t.y, k)), py = t.y * c + c * (0.2 + 0.6 * hash01(t.x, t.y, k + 7));
          const r = c * (1.1 + 0.5 * hash01(t.x, t.y, k + 3));
          const base = v.parted ? 0.08 : 0.82;
          const img = this.add(this.scene.add.image(px, py, 'fk-x-puff').setDisplaySize(r, r).setAlpha(base).setTint(0xeef2f8));
          this.puffs.push({ img, x: px, y: py, r, seed: hash01(t.x, t.y, k + 11) * 1000, veil: vi, base });
        }
      }
      if (!v.parted && v.name && v.tiles[0]) this.label(v.tiles[0], v.name.replace(/^the /i, ''), '#dfe8f4', 2);
    });
    // wonders
    x.wonders.forEach((w) => {
      const f = byId.get(w.fixture);
      if (!f) return;
      const look = lookOf(w.look);
      const t = f.tile, mx = t.x * c + c / 2, my = t.y * c + c / 2;
      const seed = hash01(t.x, t.y, 5);
      const glow = this.add(this.scene.add.image(mx, my, 'fk-x-glow').setDisplaySize(c * 2.4, c * 2.4).setTint(look.glow).setBlendMode(Phaser.BlendModes.ADD).setAlpha(w.found ? 0.25 : 0.5));
      // a soft shadow it stands on
      const shade = this.add(this.scene.add.graphics());
      if (look.stir !== 'hover' && look.family !== 'light' && look.key !== 'MushroomRing') shade.fillStyle(0x000000, 0.28).fillEllipse(mx, my + c * 0.38, c * 0.8, c * 0.22);
      const parts: Phaser.GameObjects.Components.Transform[] = [];
      let body: Phaser.GameObjects.Image | undefined;
      let sheen: Phaser.GameObjects.Image | undefined;
      // the commissioned piece where there is one: drawn as it is, with no tint, sheen or vector touch
      const art = look.art && this.scene.textures.exists(PUZZLE_ATLAS.key) && this.scene.textures.get(PUZZLE_ATLAS.key).has(look.art) ? look.art : undefined;
      const drawArt = (key: string): Phaser.GameObjects.Image => {
        const img = this.add(this.scene.add.image(mx, my, PUZZLE_ATLAS.key, key));
        img.setScale((c * look.scale) / Math.max(1, img.width));
        if (look.family === 'creature' && seed > 0.5) img.setFlipX(true);
        return img;
      };
      if (art && look.extra !== 'beam') {
        body = drawArt(art);
      } else if (look.extra === 'ring') {
        // a ring of toadstools round a pale patch
        shade.fillStyle(0xfff4e0, 0.12).fillCircle(mx, my, c * 0.42);
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2 + seed;
          const m = this.image(look.frame, mx + Math.cos(a) * c * 0.36, my + Math.sin(a) * c * 0.3, c * look.scale * (0.85 + 0.3 * hash01(t.x, t.y, i)));
          if (m) parts.push(m);
        }
      } else if (look.extra === 'beam') {
        // a shaft of light falling onto the square, a star burning where it lands
        const g = this.add(this.scene.add.graphics());
        g.fillStyle(0xffffff, 0.16).fillTriangle(mx - c * 0.12, my - c * 1.8, mx + c * 0.12, my - c * 1.8, mx + c * 0.42, my + c * 0.3);
        g.fillStyle(0xffffff, 0.16).fillTriangle(mx - c * 0.12, my - c * 1.8, mx - c * 0.42, my + c * 0.3, mx + c * 0.42, my + c * 0.3);
        g.setBlendMode(Phaser.BlendModes.ADD);
        parts.push(g);
        body = art ? drawArt(art) : this.image(look.frame, mx, my, c * look.scale);
      } else if (look.extra === 'tower') {
        // a slim tower: a spire of stone with a lit window and a pointed roof
        const g = this.add(this.scene.add.graphics());
        g.fillStyle(0x6c6658, 1).fillRect(mx - c * 0.2, my - c * 0.55, c * 0.4, c * 0.95);
        g.fillStyle(0x8a8272, 1).fillRect(mx - c * 0.2, my - c * 0.55, c * 0.12, c * 0.95);
        g.fillStyle(0x3a3448, 1).fillTriangle(mx - c * 0.28, my - c * 0.52, mx + c * 0.28, my - c * 0.52, mx, my - c * 1.05);
        g.fillStyle(0xffd060, 1).fillRect(mx - c * 0.05, my - c * 0.32, c * 0.1, c * 0.14);
        g.lineStyle(1, 0x2a2620, 1).strokeRect(mx - c * 0.2, my - c * 0.55, c * 0.4, c * 0.95);
        parts.push(g);
      } else {
        body = this.image(look.frame, mx, my, c * look.scale);
        if (body) {
          if (look.fill !== undefined) body.setTint(look.fill).setTintMode(Phaser.TintModes.FILL);
          else if (look.tint !== undefined) body.setTint(look.tint);
          if (look.alpha !== undefined) body.setAlpha(look.alpha);
          // creatures face where their seed says (the turn flips them now and then)
          if (look.family === 'creature' && seed > 0.5) body.setFlipX(true);
          if (look.sheen !== undefined) {
            // glass, ice and light: a bright copy laid over it in light
            const sh = this.image(look.frame, mx, my, c * look.scale);
            // over a silhouette, the frame itself shows through faintly as the detail in the glass
            if (sh) { if (look.fill !== undefined) sh.setBlendMode(Phaser.BlendModes.MULTIPLY); else sh.setTint(look.sheen).setBlendMode(Phaser.BlendModes.ADD); sh.setAlpha(0.55).setFlipX(body.flipX); sheen = sh; }
          }
        } else {
          // no atlas (tests, dev): a glowing gem in its colour
          const g = this.add(this.scene.add.graphics());
          g.fillStyle(look.glow, 1).fillCircle(mx, my, c * 0.28);
          parts.push(g);
        }
      }
      const extra = art ? undefined : this.extra(look, mx, my, body?.flipX ?? false);
      if (extra) parts.push(extra);
      // sparks circling an unbeheld wonder
      const sparks: Phaser.GameObjects.Image[] = [];
      if (!w.found) for (let i = 0; i < 3; i++) sparks.push(this.add(this.scene.add.image(mx, my, 'fk-x-spark').setTint(look.glow).setBlendMode(Phaser.BlendModes.ADD)));
      // a soft gold ring once beheld
      let ring: Phaser.GameObjects.Graphics | undefined;
      if (w.found) {
        ring = this.add(this.scene.add.graphics());
        ring.lineStyle(1, GOLD, 0.55).strokeEllipse(mx, my + c * 0.3, c * 0.95, c * 0.34);
      }
      this.label(t, cap(wonderName(w, f)).replace(/^(A|An|The) /, (m) => m), w.found ? '#ffe7a3' : '#e9f2ff');
      this.wonders.push({ id: w.fixture, tile: t, look, found: w.found, ...(body ? { body } : {}), ...(sheen ? { sheen } : {}), parts, glow, sparks, ...(ring ? { ring } : {}), seed });
    });
  }

  /** docs/138 XO8: one camp's dressing on its squares, and its name once seen. */
  private camp(camp: ExploreCamp): void {
    const c = this.cell;
    const dress = campDressOf(camp.dress);
    const tiles = camp.tiles.length ? camp.tiles : camp.name ? [camp.at] : [];
    tiles.forEach((t, i) => {
      const mx = t.x * c + c / 2, my = t.y * c + c / 2;
      const h = hash01(t.x, t.y, 17 + i);
      const pick = <T,>(xs: readonly T[]): T => xs[Math.floor(h * xs.length) % xs.length]!;
      const g = () => this.add(this.scene.add.graphics());
      switch (dress) {
        case 'Fire': {
          if (i === 0) {
            const ring = g();
            // a ring of hearth stones round blackened ground
            ring.fillStyle(0x2a1e14, 0.85).fillEllipse(mx, my + c * 0.2, c * 0.8, c * 0.42);
            for (let k = 0; k < 9; k++) {
              const a = (k / 9) * Math.PI * 2;
              const px = mx + Math.cos(a) * c * 0.4, py = my + c * 0.2 + Math.sin(a) * c * 0.21;
              ring.fillStyle(0x4a4440, 1).fillEllipse(px, py + 1, c * 0.16, c * 0.11);
              ring.fillStyle(0x8a847c, 1).fillEllipse(px, py, c * 0.15, c * 0.1);
            }
            const glow = this.add(this.scene.add.image(mx, my, 'fk-x-glow').setTint(0xff9a40).setBlendMode(Phaser.BlendModes.ADD).setDisplaySize(c * 2.2, c * 2.2).setAlpha(0.45));
            const img = this.image('world_0097', mx, my - c * 0.05, c * 0.8);
            if (img) this.flickers.push({ img, frames: ['world_0097', 'world_0098'], seed: h * 10, glow });
          } else this.logs(mx, my, h);
          break;
        }
        case 'Tents': {
          if (i % 2 === 0) this.tent(mx, my, h); else this.image(pick(['item_151', 'world_0435']), mx, my + c * 0.1, c * 0.6);
          break;
        }
        case 'Webs': {
          const img = this.image(pick(['world_0148', 'world_0149', 'world_0144', 'world_0145']), mx, my, c);
          img?.setAlpha(0.85).setTint(0xe8eef4);
          if (i === 0) { const k = g(); k.fillStyle(0xeae6dc, 0.9).fillEllipse(mx + c * 0.18, my + c * 0.1, c * 0.16, c * 0.3); }
          break;
        }
        case 'Nest': {
          const k = g();
          k.lineStyle(2, 0x6a4a2a, 1);
          for (let a = 0; a < 6; a++) { const r = a * 1.1 + h; k.lineBetween(mx + Math.cos(r) * c * 0.38, my + Math.sin(r) * c * 0.2, mx - Math.cos(r) * c * 0.38, my - Math.sin(r) * c * 0.2 + c * 0.05); }
          if (i === 0) { k.fillStyle(0xf0ead8, 1).fillEllipse(mx - c * 0.08, my, c * 0.14, c * 0.18).fillEllipse(mx + c * 0.08, my + c * 0.02, c * 0.14, c * 0.18); }
          else this.image(pick(['item_096', 'item_097', 'item_098']), mx, my, c * 0.5);
          break;
        }
        case 'Burrow': {
          if (i % 2 === 0) { const k = g(); k.fillStyle(0x6b5235, 1).fillEllipse(mx, my + c * 0.1, c * 0.85, c * 0.45); k.fillStyle(0x1a120a, 1).fillEllipse(mx, my + c * 0.08, c * 0.42, c * 0.24); }
          else this.image(pick(['world_0620', 'world_0619']), mx, my, c * 0.7)?.setTint(0xb89a70);
          break;
        }
        case 'Bones': this.image(i === 0 ? 'world_0096' : pick(['world_0090', 'world_0091', 'world_0092', 'world_0093']), mx, my, c); break;
        case 'Totems': {
          const k = g();
          k.fillStyle(0x5a3a22, 1).fillRect(mx - c * 0.05, my - c * 0.45, c * 0.1, c * 0.85);
          k.fillStyle(pick([0xc0392b, 0x2e86c1, 0xd4ac0d]), 1).fillRect(mx - c * 0.08, my - c * 0.1, c * 0.16, c * 0.06);
          this.image(pick(['item_133', 'item_135', 'item_134']), mx, my - c * 0.42, c * 0.48);
          break;
        }
        case 'Wagons': this.image(pick(['world_0435', 'world_0268', 'item_151', 'world_0269']), mx, my, c * 0.9); break;
        case 'Hive': {
          const k = g();
          if (i === 0) {
            k.fillStyle(0x7a5a18, 1).fillEllipse(mx, my + c * 0.05, c * 0.62, c * 0.7);
            k.fillStyle(0xd8a030, 1).fillEllipse(mx, my, c * 0.56, c * 0.64);
            k.lineStyle(1, 0x8a6418, 1); for (let b = -2; b <= 2; b++) k.lineBetween(mx - c * 0.26, my + b * c * 0.11, mx + c * 0.26, my + b * c * 0.11);
            k.fillStyle(0x2a1a08, 1).fillEllipse(mx, my + c * 0.22, c * 0.14, c * 0.1);
          } else { k.fillStyle(0xd8a030, 0.9).fillEllipse(mx, my + c * 0.15, c * 0.5, c * 0.25); k.lineStyle(1, 0x8a6418, 0.8).strokeEllipse(mx, my + c * 0.15, c * 0.5, c * 0.25); }
          break;
        }
        case 'Pool': {
          const img = this.image('world_1737', mx, my, c);
          if (img) img.setTint(0x405848); else { const k = g(); k.fillStyle(0x203828, 0.9).fillEllipse(mx, my, c * 0.9, c * 0.6); }
          break;
        }
        case 'Ruin': this.image(pick(['world_0087', 'world_0088', 'world_0089']), mx, my, c); break;
        case 'Altar': {
          if (i === 0) this.image('world_0492', mx, my, c);
          else this.image(pick(['item_145', 'item_145', 'item_146']), mx, my, c * 0.6);
          if (i > 0) this.add(this.scene.add.image(mx, my - c * 0.15, 'fk-x-glow').setTint(0xffd070).setBlendMode(Phaser.BlendModes.ADD).setDisplaySize(c * 0.9, c * 0.9).setAlpha(0.35));
          break;
        }
      }
    });
    if (camp.name) this.label(camp.at, cap(camp.name.replace(/^(a|an|the) /i, '')), '#f2c6a0', -c * 0.9);
  }

  /** Round a fire: a log to sit on, else a bedroll, else a pot, by the square's dice. */
  private logs(mx: number, my: number, h: number): void {
    const c = this.cell, k = this.add(this.scene.add.graphics());
    k.fillStyle(0x000000, 0.25).fillEllipse(mx, my + c * 0.3, c * 0.75, c * 0.18);
    if (h < 0.45) {
      const y = my + c * 0.12, x0 = mx - c * 0.32, len = c * 0.64, th = c * 0.2;
      k.fillStyle(0x3e2614, 1).fillRoundedRect(x0, y, len, th, th / 2);
      k.fillStyle(0x6e4626, 1).fillRoundedRect(x0, y, len, th * 0.6, th * 0.3);
      k.fillStyle(0xc49a64, 1).fillEllipse(x0 + len - th * 0.3, y + th / 2, th * 0.55, th * 0.9);
      k.lineStyle(1, 0x7a5430, 1).strokeEllipse(x0 + len - th * 0.3, y + th / 2, th * 0.3, th * 0.5);
    } else if (h < 0.8) {
      // a bedroll: a rolled blanket with a strap
      const col = [0x7a3a2a, 0x3a5a6a, 0x6a6a3a][Math.floor(h * 30) % 3]!;
      k.fillStyle(col, 1).fillRoundedRect(mx - c * 0.34, my - c * 0.02, c * 0.68, c * 0.3, c * 0.12);
      k.fillStyle(0xffffff, 0.15).fillRoundedRect(mx - c * 0.34, my - c * 0.02, c * 0.68, c * 0.1, c * 0.05);
      k.fillStyle(0x2a1a10, 1).fillRect(mx - c * 0.05, my - c * 0.02, c * 0.06, c * 0.3);
    } else this.image('item_151', mx, my + c * 0.08, c * 0.6);
  }

  private tent(mx: number, my: number, h: number): void {
    const c = this.cell, k = this.add(this.scene.add.graphics());
    const cloth = [0xb89a6a, 0x8a7a5a, 0x9a5a3a, 0x6a7a5a][Math.floor(h * 4) % 4]!;
    k.fillStyle(0x000000, 0.25).fillEllipse(mx, my + c * 0.38, c * 0.9, c * 0.2);
    k.fillStyle(cloth, 1).fillTriangle(mx - c * 0.42, my + c * 0.38, mx + c * 0.42, my + c * 0.38, mx, my - c * 0.36);
    k.fillStyle(0x000000, 0.2).fillTriangle(mx, my - c * 0.36, mx + c * 0.42, my + c * 0.38, mx + c * 0.12, my + c * 0.38);
    k.fillStyle(0x1a120a, 1).fillTriangle(mx - c * 0.1, my + c * 0.38, mx + c * 0.1, my + c * 0.38, mx, my + c * 0.02);
    k.lineStyle(1, 0x3a2a18, 1).lineBetween(mx, my - c * 0.36, mx, my - c * 0.46);
  }

  /** docs/138 XO8: a drifting "z" over every foe still asleep in its camp (not in an awake lair). */
  private sleepers(crawl: CrawlView | undefined, units: readonly UnitState[] | undefined): void {
    if (!crawl || !units) return;
    const awake = awakeFoes(crawl);
    const c = this.cell;
    for (const u of units) {
      if (u.faction !== 'enemy' || u.onBoard === false || u.downed || u.hp <= 0 || awake.has(u.unitId)) continue;
      const size = u.size ?? 1;
      const x = (u.tile.x + size) * c - c * 0.15, y = u.tile.y * c + c * 0.05;
      const txt = this.scene.add.text(x, y, 'z', { fontFamily: 'Georgia, serif', fontSize: '7px', fontStyle: '700', color: '#dfe8ff', stroke: '#0b0f1a', strokeThickness: 2 })
        .setOrigin(0.5, 1).setResolution(Math.max(4, this.dpr * 4));
      this.high.add(txt);
      this.objects.push(txt);
      this.snores.push({ txt, x, y, seed: hash01(u.tile.x, u.tile.y, u.unitId) * 10 });
    }
  }

  /** docs/138 XO5: the wonder blesses a hero — a soft glow round them and gold motes rising. */
  bless(at: Tile, ms: number): void {
    const c = this.cell, mx = at.x * c + c / 2, my = at.y * c + c / 2;
    const dur = Math.max(1200, ms * 1.6);
    const glow = oneShot(this.scene, this.scene.add.image(mx, my, 'fk-x-glow')).setTint(0xffe7a3).setBlendMode(Phaser.BlendModes.ADD).setDisplaySize(c * 0.8, c * 0.8).setAlpha(0.9);
    this.high.add(glow);
    this.scene.tweens.add({ targets: glow, displayWidth: c * 2.4, displayHeight: c * 2.4, alpha: 0, duration: dur, ease: 'Sine.easeOut', onComplete: () => glow.destroy() });
    if (this.reduced) return;
    for (let i = 0; i < 12; i++) {
      const sx = mx + (hash01(at.x, at.y, i) - 0.5) * c * 0.9, sy = my + c * 0.3;
      const m = oneShot(this.scene, this.scene.add.image(sx, sy, i % 3 ? 'fk-x-mote' : 'fk-x-spark')).setTint(i % 2 ? 0xffe7a3 : 0xfff6d8).setBlendMode(Phaser.BlendModes.ADD).setScale(i % 3 ? 0.8 : 1.1).setAlpha(0);
      this.high.add(m);
      this.scene.tweens.add({ targets: m, y: sy - c * (1 + hash01(at.x, at.y, i + 30)), alpha: { from: 1, to: 0 }, delay: i * (dur / 30), duration: dur * 0.8, ease: 'Sine.easeOut', onComplete: () => m.destroy() });
    }
  }

  /** The small vector touch a look carries over its frame. */
  private extra(look: LookDef, mx: number, my: number, flip: boolean): Phaser.GameObjects.Graphics | undefined {
    if (!look.extra || look.extra === 'ring' || look.extra === 'beam' || look.extra === 'tower') return undefined;
    const c = this.cell, s = flip ? -1 : 1;
    const g = this.add(this.scene.add.graphics());
    switch (look.extra) {
      case 'antlers': {
        // branching antlers over the beast's head (it faces west unflipped)
        const hx = mx - s * c * 0.26, hy = my - c * 0.3;
        g.lineStyle(1.2, 0xf4ecd8, 1);
        for (const side of [-1, 1]) {
          const bx = hx + side * c * 0.07;
          g.lineBetween(bx, hy, bx + side * c * 0.12, hy - c * 0.3);
          g.lineBetween(bx + side * c * 0.06, hy - c * 0.14, bx + side * c * 0.2, hy - c * 0.18);
          g.lineBetween(bx + side * c * 0.1, hy - c * 0.24, bx + side * c * 0.04, hy - c * 0.36);
        }
        break;
      }
      case 'eyes': {
        const ex = look.key === 'Owl' ? mx : mx - s * c * 0.08, ey = look.key === 'Owl' ? my - c * 0.18 : my - c * 0.12;
        g.fillStyle(0xfff3a0, 1).fillCircle(ex - c * 0.08, ey, 1.4).fillCircle(ex + c * 0.08, ey, 1.4);
        g.fillStyle(0x0b0f1a, 1).fillCircle(ex - c * 0.08, ey, 0.6).fillCircle(ex + c * 0.08, ey, 0.6);
        break;
      }
      case 'lotus': {
        g.fillStyle(0xffb0d8, 1).fillCircle(mx + c * 0.06, my - c * 0.04, c * 0.12);
        g.fillStyle(0xffe6f2, 1).fillCircle(mx + c * 0.06, my - c * 0.07, c * 0.06);
        break;
      }
      case 'roses': {
        for (let i = 0; i < 4; i++) g.fillStyle(0xff4a6a, 1).fillCircle(mx + c * (hash01(i, 1) - 0.5) * 0.6, my + c * (hash01(i, 2) - 0.6) * 0.6, 1.6);
        break;
      }
      case 'strings': {
        g.lineStyle(0.6, 0xfff6d0, 0.9);
        for (let i = 0; i < 4; i++) g.lineBetween(mx - c * 0.18 + i * c * 0.1, my - c * 0.3 + i * c * 0.04, mx - c * 0.18 + i * c * 0.1, my + c * 0.3 - i * c * 0.06);
        break;
      }
      case 'orbit': {
        g.lineStyle(0.8, 0xffe08a, 0.8).strokeEllipse(mx, my, c * 0.95, c * 0.38);
        g.fillStyle(0x9fd8ff, 1).fillCircle(mx + c * 0.47, my, 1.6);
        g.fillStyle(0xff9a6a, 1).fillCircle(mx - c * 0.3, my + c * 0.14, 1.2);
        break;
      }
      case 'cairn': {
        g.fillStyle(0xd8d2c0, 1).fillEllipse(mx, my - c * 0.28, c * 0.28, c * 0.16);
        g.fillStyle(0xc8c0ac, 1).fillEllipse(mx + c * 0.02, my - c * 0.4, c * 0.18, c * 0.11);
        break;
      }
      case 'rune': {
        g.lineStyle(1, 0xc8a8ff, 0.95);
        g.lineBetween(mx, my - c * 0.3, mx, my + c * 0.05); g.lineBetween(mx - c * 0.08, my - c * 0.18, mx + c * 0.08, my - c * 0.08);
        break;
      }
    }
    return g;
  }

  /** The air's motes over the whole board and its wash (rebuilt only when the air or the board changes). */
  /**
   * docs/118 PT12: under a blind fog the air keeps to the squares the party has seen (`field`, the
   * board with the unseen as `Void`): its wash and motes would otherwise draw the board's whole
   * rectangle over the dark. `undefined`: the whole board, as before.
   */
  setSeen(field: FieldShape | undefined): void {
    this.seen = field;
    this.seenKey = field ? field.tiles.map((t) => (t?.terrain === 'Void' ? 0 : 1)).join('') : '';
    if (this.lastAir) this.setAir(this.lastAir.air, this.lastAir.board);
  }

  private setAir(air: number | undefined, board: { w: number; h: number }): void {
    this.lastAir = { air, board };
    const seen = this.seen && this.seen.w === board.w && this.seen.h === board.h ? this.seen : undefined;
    const key = air === undefined ? '' : `${air}|${board.w}x${board.h}|${this.reduced}|${seen ? this.seenKey : ''}`;
    if (key === this.airKey) return;
    this.airKey = key;
    for (const e of this.emitters) e.destroy();
    this.emitters = [];
    this.wash?.destroy();
    this.wash = undefined;
    if (air === undefined) return;
    const def = airOf(air);
    const c = this.cell, W = board.w * c, H = board.h * c;
    if (seen) {
      // only over the squares seen, merged into a few rects (the Graphics redraws them every frame)
      const g = this.scene.add.graphics();
      g.fillStyle(def.tint, def.wash);
      const on: Tile[] = [];
      for (let y = 0; y < seen.h; y++) for (let x = 0; x < seen.w; x++) { const t = seen.tiles[y * seen.w + x]; if (t && t.terrain !== 'Void') on.push({ x, y }); }
      for (const r of mergeRuns(on)) g.fillRect(r.x * c, r.y * c, r.w * c, r.h * c);
      this.wash = g;
    } else this.wash = this.scene.add.rectangle(0, 0, W, H, def.tint, def.wash).setOrigin(0, 0);
    this.high.add(this.wash);
    if (this.reduced) return;
    // under a blind fog every mote fades out where the seen field ends
    const fade = seen ? fieldFade(seen, c) : undefined;
    type Alpha = Phaser.Types.GameObjects.Particles.ParticleEmitterConfig['alpha'];
    const keep = (a: Alpha): Alpha => {
      if (!fade) return a;
      const at = (p: Phaser.GameObjects.Particles.Particle) => fade(p.x, p.y, c / 2);
      if (a && typeof a === 'object' && 'onUpdate' in a && a.onUpdate) {
        const f = a.onUpdate;
        return { onEmit: () => 0, onUpdate: (p: Phaser.GameObjects.Particles.Particle, k: string, t: number, v: number) => f(p, k, t, v) * at(p) };
      }
      if (a && typeof a === 'object' && 'start' in a) {
        const s0 = Number(a.start), s1 = Number(a.end ?? a.start);
        return { onEmit: () => 0, onUpdate: (p: Phaser.GameObjects.Particles.Particle, _k: string, t: number) => (s0 + (s1 - s0) * t) * at(p) };
      }
      return a;
    };
    const zone: Phaser.Types.GameObjects.Particles.ParticleEmitterRandomZoneConfig = {
      type: 'random', source: { getRandomPoint: (p: Phaser.Types.Math.Vector2Like) => { p.x = Math.random() * W; p.y = Math.random() * H; } },
    };
    const cells = board.w * board.h;
    const emit = (tex: string, perCell: number, cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig) => {
      const rate = Math.min(160, Math.max(2, cells * perCell));
      const e = this.scene.add.particles(0, 0, tex, { emitZone: zone, frequency: 1000 / rate, quantity: 1, tint: def.colours, blendMode: Phaser.BlendModes.ADD, advance: 6000, ...cfg, alpha: keep(cfg.alpha) });
      this.high.add(e);
      this.emitters.push(e);
    };
    switch (def.motes) {
      case 'mist':
        emit('fk-x-puff', 0.012, { lifespan: { min: 5000, max: 9000 }, speedX: { min: 4, max: 12 }, speedY: { min: -3, max: 3 }, scale: { start: 0.6, end: 1.4 }, alpha: { onEmit: () => 0, onUpdate: (_p: unknown, _k: string, t: number) => Math.sin(t * Math.PI) * 0.22 }, blendMode: Phaser.BlendModes.NORMAL });
        break;
      case 'gold':
        emit('fk-x-mote', 0.05, { lifespan: { min: 3500, max: 6500 }, speedY: { min: -10, max: -3 }, speedX: { min: -4, max: 4 }, scale: { min: 0.3, max: 0.7 }, alpha: { start: 0.9, end: 0 } });
        break;
      case 'dust':
        emit('fk-x-mote', 0.01, { lifespan: { min: 4000, max: 8000 }, speedY: { min: -2, max: 2 }, speedX: { min: -2, max: 2 }, scale: { min: 0.2, max: 0.35 }, alpha: { start: 0.6, end: 0 } });
        break;
      case 'dream':
        emit('fk-x-mote', 0.025, { lifespan: { min: 3500, max: 6500 }, speedY: { min: -8, max: -2 }, speedX: { min: -8, max: 8 }, scale: { start: 0.7, end: 0.1 }, alpha: { start: 0.8, end: 0 } });
        break;
      case 'pollen':
        emit('fk-x-mote', 0.025, { lifespan: { min: 3000, max: 6000 }, speedX: { min: 4, max: 14 }, speedY: { min: -4, max: 6 }, scale: { min: 0.2, max: 0.45 }, alpha: { start: 0.9, end: 0 } });
        emit('fk-x-petal', 0.006, { lifespan: { min: 4000, max: 7000 }, speedX: { min: 6, max: 16 }, speedY: { min: 4, max: 10 }, rotate: { start: 0, end: 360 }, alpha: { start: 0.9, end: 0 }, tint: [0xffb8e0, 0xffd8ee], blendMode: Phaser.BlendModes.NORMAL });
        break;
      case 'fireflies':
        emit('fk-x-mote', 0.04, { lifespan: { min: 3000, max: 5500 }, speedX: { min: -6, max: 6 }, speedY: { min: -6, max: 6 }, scale: { min: 0.35, max: 0.6 }, alpha: { onEmit: () => 0, onUpdate: (_p: unknown, _k: string, t: number) => Math.max(0, Math.sin(t * Math.PI * 3)) } });
        break;
      case 'spores':
        emit('fk-x-mote', 0.015, { lifespan: { min: 5000, max: 9000 }, speedY: { min: -5, max: -1 }, speedX: { min: -2, max: 2 }, scale: { min: 0.2, max: 0.45 }, alpha: { start: 0.7, end: 0 } });
        break;
      case 'snow':
        emit('fk-x-mote', 0.03, { lifespan: { min: 4000, max: 7000 }, speedY: { min: 6, max: 14 }, speedX: { min: -4, max: 2 }, scale: { min: 0.2, max: 0.45 }, alpha: { start: 0.9, end: 0 }, blendMode: Phaser.BlendModes.NORMAL });
        break;
    }
  }

  /** Per frame: wonders stir and glimmer, mist drifts, a parting veil blows apart. */
  update(time: number): void {
    this.now = time;
    const still = this.reduced;
    const c = this.cell;
    for (const w of this.wonders) {
      const t = time / 1000 + w.seed * 10;
      const mx = w.tile.x * c + c / 2, my = w.tile.y * c + c / 2;
      const b = w.body;
      if (b && !still) {
        b.setPosition(mx, my).setScale((c * w.look.scale) / Math.max(1, b.width));
        switch (w.look.stir) {
          case 'breathe': b.setScale(b.scaleX, b.scaleY * (1 + 0.04 * Math.sin(t * 2.2))); b.y = my - c * 0.02 * Math.sin(t * 2.2); break;
          case 'hover': b.y = my - c * (0.12 + 0.08 * Math.sin(t * 1.8)); b.x = mx + c * 0.05 * Math.sin(t * 0.9); break;
          case 'sway': b.setAngle(4 * Math.sin(t * 1.4)); break;
          case 'turn': if (Math.sin(t * 0.35) > 0.97) b.setFlipX(w.seed <= 0.5); else if (Math.sin(t * 0.35) < -0.97) b.setFlipX(w.seed > 0.5); b.y = my - c * 0.015 * Math.sin(t * 2); break;
          case 'ripple': b.setScale(b.scaleX * (1 + 0.03 * Math.sin(t * 1.6)), b.scaleY * (1 + 0.03 * Math.sin(t * 1.6))); break;
          case 'pulse': case 'still': break;
        }
      }
      if (b && w.sheen) w.sheen.setPosition(b.x, b.y).setScale(b.scaleX, b.scaleY).setAngle(b.angle).setFlipX(b.flipX).setAlpha((w.look.fill !== undefined ? 0.35 : 0.4) + 0.25 * Math.sin(t * 1.7));
      if (!still && w.look.stir === 'sway') for (const p of w.parts) p.setAngle(3 * Math.sin(t * 1.4));
      const pulse = 0.5 + 0.5 * Math.sin(t * (w.look.stir === 'pulse' ? 2.4 : 1.2));
      w.glow.setAlpha((w.found ? 0.16 : 0.32) + (w.found ? 0.08 : 0.22) * (still ? 0.5 : pulse));
      w.sparks.forEach((s, i) => {
        if (still) { s.setPosition(mx + (i - 1) * c * 0.35, my - c * 0.45).setAlpha(0.8); return; }
        const a = t * 1.3 + (i * Math.PI * 2) / 3;
        s.setPosition(mx + Math.cos(a) * c * 0.5, my - c * 0.1 + Math.sin(a) * c * 0.32 - c * 0.1 * Math.sin(t * 2 + i));
        s.setAlpha(0.35 + 0.65 * Math.max(0, Math.sin(t * 3 + i * 2)));
        s.setScale(0.6 + 0.5 * Math.max(0, Math.sin(t * 3 + i * 2)));
      });
    }
    // docs/138 XO8: camp fires flicker, sleepers' z's drift up and fade
    for (const f of this.flickers) {
      const t = time / 1000 + f.seed;
      if (!still) f.img.setFrame(f.frames[Math.floor(t * 6) % f.frames.length]!);
      f.glow?.setAlpha(still ? 0.4 : 0.32 + 0.16 * Math.sin(t * 7) * Math.sin(t * 3.1));
    }
    for (const z of this.snores) {
      const k = still ? 0.4 : ((time / 1000 + z.seed) % 2.4) / 2.4;
      z.txt.setPosition(z.x + c * 0.12 * k, z.y - c * 0.35 * k).setAlpha(still ? 0.9 : Math.sin(k * Math.PI)).setScale(0.7 + 0.6 * k);
    }
    for (const p of this.puffs) {
      const t = time / 1000 + p.seed;
      let a = p.base, dx = still ? 0 : Math.sin(t * 0.35) * c * 0.18, dy = still ? 0 : Math.cos(t * 0.27) * c * 0.1, s = 1 + (still ? 0 : 0.08 * Math.sin(t * 0.5));
      const began = p.veil >= 0 ? this.parting.get(p.veil) : undefined;
      if (began !== undefined) {
        const k = Math.min(1, (time - began) / 1400);
        a = p.base * (1 - k) + 0.08 * k;
        const ang = p.seed;
        dx += Math.cos(ang) * c * 1.4 * k; dy += Math.sin(ang) * c * 1.4 * k - c * 0.4 * k; s *= 1 + k;
      } else if (!still) a = p.base * (0.85 + 0.15 * Math.sin(t * 0.6));
      p.img.setPosition(p.x + dx, p.y + dy).setDisplaySize(p.r * s, p.r * s).setAlpha(a);
    }
  }

  /** docs/61 EX6: a wonder flares as it is beheld — a burst of light and sparks rising. */
  flare(fixture: number, ms: number): void {
    const w = this.wonders.find((x) => x.id === fixture);
    if (!w) return;
    const c = this.cell, mx = w.tile.x * c + c / 2, my = w.tile.y * c + c / 2;
    const burst = oneShot(this.scene, this.scene.add.image(mx, my, 'fk-x-glow')).setTint(w.look.glow).setBlendMode(Phaser.BlendModes.ADD).setDisplaySize(c * 1.5, c * 1.5).setAlpha(1);
    this.high.add(burst);
    this.scene.tweens.add({ targets: burst, displayWidth: c * 7, displayHeight: c * 7, alpha: 0, duration: Math.max(1600, ms * 1.6), ease: 'Sine.easeOut', onComplete: () => burst.destroy() });
    const g = oneShot(this.scene, this.scene.add.graphics()); this.high.add(g);
    const st = { r: c * 0.3, a: 1 };
    const paint = () => { g.clear(); g.lineStyle(2, w.look.glow, st.a).strokeCircle(mx, my, st.r); g.lineStyle(1, GOLD, st.a * 0.7).strokeCircle(mx, my, st.r * 0.6); };
    paint();
    this.scene.tweens.add({ targets: st, r: c * 2.6, a: 0, duration: Math.max(1600, ms * 1.6), ease: 'Quad.easeOut', onUpdate: paint, onComplete: () => g.destroy() });
    for (let i = 0; i < 10; i++) {
      const s = oneShot(this.scene, this.scene.add.image(mx, my, 'fk-x-spark')).setTint(w.look.glow).setBlendMode(Phaser.BlendModes.ADD).setScale(1.4);
      this.high.add(s);
      const a = (i / 10) * Math.PI * 2;
      this.scene.tweens.add({ targets: s, x: mx + Math.cos(a) * c * 1.2, y: my + Math.sin(a) * c * 0.8 - c * 0.6, alpha: 0, scale: 0.3, duration: Math.max(1600, ms * 1.6), ease: 'Quad.easeOut', onComplete: () => s.destroy() });
    }
  }

  /** docs/61 EX9: the veil `index` blows apart (its puffs drift off and thin to wisps). */
  part(index: number): void { this.parting.set(index, this.now); }

  /** docs/61 EX5 `Sight`: a ring of pale light runs out over the land the vantage shows. */
  vantage(at: Tile, ms: number, reach = 9): void {
    const c = this.cell, mx = at.x * c + c / 2, my = at.y * c + c / 2;
    const g = oneShot(this.scene, this.scene.add.graphics()); this.high.add(g);
    const st = { r: c, a: 0.9 };
    const paint = () => {
      g.clear();
      // the four-way grid's reach is a diamond
      const r = st.r;
      g.lineStyle(2, 0xf4f8ff, st.a).strokePoints([new Phaser.Math.Vector2(mx, my - r), new Phaser.Math.Vector2(mx + r, my), new Phaser.Math.Vector2(mx, my + r), new Phaser.Math.Vector2(mx - r, my)], true);
    };
    paint();
    this.scene.tweens.add({ targets: st, r: c * (reach + 0.5), a: 0, duration: Math.max(600, ms * 1.4), ease: 'Sine.easeOut', onUpdate: paint, onComplete: () => g.destroy() });
  }

  destroy(): void {
    for (const o of this.objects) o.destroy();
    for (const e of this.emitters) e.destroy();
    this.wash?.destroy();
    this.objects = []; this.emitters = []; this.wonders = []; this.puffs = []; this.flickers = []; this.snores = [];
    this.airKey = '';
  }
}

/** Which way off the board a square at the edge looks (a unit step), the nearest edge's. */
export function edgeOut(t: Tile, w: number, h: number): { x: number; y: number } {
  const d = [t.x, w - 1 - t.x, t.y, h - 1 - t.y];
  const i = d.indexOf(Math.min(...d));
  return [{ x: -1, y: 0 }, { x: 1, y: 0 }, { x: 0, y: -1 }, { x: 0, y: 1 }][i]!;
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
